<template>
  <div class="container form-section" v-if="userId">
    <div class="row">
      <div class="col-12">
        <h2 class="section-title">Gestion des adresses</h2>
        <div class="section-body">

          <!-- Add New Address Button -->
          <div class="row mb-4">
            <div class="col-12">
              <button @click="startAddingAddress" class="btn btn-lg btn-primary">
                <i class="fas fa-plus"></i> Ajouter une nouvelle adresse
              </button>
            </div>
          </div>

          <!-- Add/Edit Address Form -->
          <div v-if="showAddForm || editingAddress" class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header">
                  <h5>{{ editingAddress ? 'Modifier l\'adresse' : 'Nouvelle adresse' }}</h5>
                </div>
                <div class="card-body">
                  <form @submit.prevent="saveAddress">
                    <div class="form-group">
                      <label for="description">Description de l'adresse *</label>
                      <textarea
                        v-model="addressForm.description"
                        class="form-control"
                        id="description"
                        rows="3"
                        placeholder="Décrivez votre adresse (ex: Maison principale, Bureau, Chez mes parents...)"
                        :class="validation_class_for('description')"
                        required>
                      </textarea>
                      <div class="invalid-feedback">
                        {{first_error_on_attribute('description')}}
                      </div>
                    </div>

                    <div class="form-group">
                      <label>Position sur la carte *</label>
                      <div class="map-section">
                        <div class="map-indication">
                          Cherchez votre adresse ou déplacez la carte pour positionner le marqueur
                        </div>
                        <div class="map-container">
                          <div class="google-map" ref="googleMap"></div>
                          <!-- Simple center pin -->
                          <div class="center-marker" :class="{ dragging: isDragging }">
                            <i class="fas fa-map-marker-alt marker-pin"></i>
                            <div class="marker-shadow"></div>
                          </div>
                          <div class="input-group mb-3 input-search">
                            <div class="input-group-prepend">
                              <span class="input-group-text">
                                <i class="fas fa-search"></i>
                              </span>
                            </div>
                            <input
                              class="form-control"
                              ref="placeSearch"
                              placeholder="Chercher votre adresse"
                            />
                          </div>
                        </div>

                        <!-- My Position Button -->
                        <div class="mt-3 text-center">
                          <button
                            type="button"
                            @click="getCurrentLocation"
                            class="btn btn-outline-primary btn-sm"
                            :disabled="gettingLocation"
                          >
                            <i :class="gettingLocation ? 'fas fa-spinner fa-spin' : 'fas fa-crosshairs'"></i>
                            {{ gettingLocation ? 'Localisation en cours...' : 'Ma position actuelle' }}
                          </button>
                        </div>

                        <div v-show="!mapInitialized" class="mt-2 text-info">
                          <i class="fas fa-spinner fa-spin"></i> Chargement de la carte...
                        </div>
                      </div>
                    </div>

                    <div class="form-group" v-if="addresses.length > 0 || editingAddress">
                      <div class="form-check">
                        <input
                          type="checkbox"
                          v-model="addressForm.is_default"
                          class="form-check-input"
                          id="isDefault"
                        >
                        <label class="form-check-label" for="isDefault">
                          Définir comme adresse par défaut
                        </label>
                      </div>
                    </div>
                    <div v-else class="alert alert-info">
                      <i class="fas fa-info-circle"></i> Cette première adresse sera automatiquement définie comme adresse par défaut.
                    </div>

                    <div class="form-group">
                      <button type="submit" class="btn btn-lg btn-primary mr-2" :disabled="!addressForm.lat || !addressForm.lng">
                        {{ editingAddress ? 'Mettre à jour' : 'Sauvegarder' }}
                      </button>
                      <button type="button" @click="cancelForm" class="btn btn-lg btn-secondary">
                        Annuler
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <!-- Addresses List -->
          <div class="row">
            <div class="col-12">
              <div v-if="addresses.length === 0 && !showAddForm && !editingAddress" class="text-center text-muted">
                <p>Aucune adresse enregistrée</p>
              </div>
              <div v-else-if="!showAddForm && !editingAddress">
                <transition-group name="address-list" tag="div">
                  <div v-for="address in addresses" :key="address.id" class="card mb-3 address-card-item">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-start">
                        <div class="flex-grow-1">
                          <div class="address-description mb-2">
                            {{ address.description }}
                            <span v-if="address.is_default" class="badge badge-success ml-2">Par défaut</span>
                          </div>
                          <div class="address-actions mt-3">
                            <button
                              @click="editAddress(address)"
                              class="btn btn-sm btn-outline-primary mr-2"
                            >
                              <i class="fas fa-edit"></i> Modifier
                            </button>
                            <button
                              v-if="!address.is_default"
                              @click="setAsDefault(address)"
                              class="btn btn-sm btn-outline-success mr-2"
                            >
                              <i class="fas fa-check"></i> Définir par défaut
                            </button>
                            <button
                              @click="deleteAddress(address)"
                              :disabled="address.is_default"
                              class="btn btn-sm btn-outline-danger"
                              :class="{ 'disabled': address.is_default }"
                            >
                              <i class="fas fa-trash"></i> Supprimer
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition-group>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Loader } from '@googlemaps/js-api-loader';
import service from './packs/services'
import zoneHelper from './packs/zone_helper'

export default {
  props: {
    userId: {
      type: [String, Number],
      required: true
    }
  },
  data: function () {
    return {
      addresses: [],
      showAddForm: false,
      editingAddress: null,
      addressForm: {
        description: '',
        lat: null,
        lng: null,
        is_default: false,
        zone_id: null
      },
      errors: [],
      map: null,
      marker: null,
      placeSearch: null,
      mapInitialized: false,
      placeSearchInitialized: false,
      isDragging: false,
      gettingLocation: false,
      zones: [],
      mapInteracted: false,  // Track if user has interacted with the map
      userDragged: false  // Track if user actually dragged the map
    }
  },
  async mounted() {
    await this.loadAddresses()
    await this.loadZones()
  },
  methods: {
    async loadAddresses() {
      try {
        this.addresses = await service.getUserAddresses(this.userId)
      } catch (err) {
        console.error('Error loading addresses:', err)
      }
    },

    async loadZones() {
      try {
        this.zones = await service.getZones()
        this.zones = this.zones.map(zone => {
          zone.formattedLocationPoints = zone.location_points.map(locationPoint =>
            ({
              lat: locationPoint.lat,
              lng: locationPoint.lng
            }))
          return zone
        })
      } catch (err) {
        console.error('Error loading zones:', err)
      }
    },

    async startAddingAddress() {
      this.showAddForm = true
      this.mapInteracted = false  // Reset interaction flag for new address
      this.userDragged = false  // Reset drag flag for new address
      // Add a small delay to ensure DOM is fully rendered
      setTimeout(async () => {
        await this.initializeGoogleMaps()
      }, 100)
    },

    async initializeGoogleMaps() {
      const googleMapLoader = new Loader({
        apiKey: 'AIzaSyBlmUqHY3MeJbIHUsJlV1Ll8aEhPwTkgRU',
        version: 'weekly',
        libraries: ['places', 'geometry']
      })
      await googleMapLoader.load()
      this.initializeMap()
    },

    initializeMap() {
      const mapContainer = this.$refs.googleMap
      if (!mapContainer) {
        console.error('Map container not found')
        return
      }

      const options = {
        mapTypeControl: false,
        center: {
          lat: 14.6945917,
          lng: -17.4659374
        },
        zoom: 12,
        disableDefaultUI: false,
        zoomControl: true,
        streetViewControl: false,
        fullscreenControl: false
      }

      this.map = new google.maps.Map(mapContainer, options)
      this.mapInitialized = true

      // Initialize place search
      this.$nextTick(() => {
        const placeSearchContainer = this.$refs.placeSearch
        if (placeSearchContainer) {
          this.placeSearch = new google.maps.places.Autocomplete(placeSearchContainer)
          this.placeSearchInitialized = true

          this.placeSearch.addListener("place_changed", () => {
            const place = this.placeSearch.getPlace()
            console.log(place);
            if (place && place.geometry) {
              this.map.setCenter({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              })
              this.map.setZoom(16)
              this.updateCenterPosition()
              this.mapInteracted = true  // User interacted via place search
            }
          })
        }
      })

      // Listen for drag start
      this.map.addListener("dragstart", () => {
        this.isDragging = true
        this.userDragged = true  // User actually started dragging
      })

      // Listen for map center changes (when user drags the map)
      this.map.addListener("center_changed", () => {
        this.updateCenterPosition()
      })

      // Listen for idle event to update position after map stops moving
      this.map.addListener("idle", () => {
        this.isDragging = false
        this.updateCenterPosition()
        // Only mark as interacted if user actually dragged the map
        if (this.userDragged) {
          this.mapInteracted = true
          this.userDragged = false  // Reset for next interaction
        }
      })

      // Set initial position
      this.updateCenterPosition()
    },

    updateCenterPosition() {
      if (!this.map) return

      const center = this.map.getCenter()
      if (center) {
        this.addressForm.lat = center.lat()
        this.addressForm.lng = center.lng()
      }
    },

    setMapCenter(lat, lng) {
      if (this.map) {
        this.map.setCenter({ lat, lng })
        this.map.setZoom(16)
      }
    },

    async getCurrentLocation() {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        this.$swal({
          icon: 'error',
          title: 'Géolocalisation non supportée',
          text: 'Votre navigateur ne supporte pas la géolocalisation.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2A6336'
        })
        return
      }

      this.gettingLocation = true

      try {
        // Get current position with options
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 60000
            }
          )
        })

        const lat = position.coords.latitude
        const lng = position.coords.longitude

        // Move map to current location
        this.setMapCenter(lat, lng)
        this.mapInteracted = true  // User interacted via location button

        // Show success message
        this.$swal({
          icon: 'success',
          title: 'Position trouvée !',
          text: 'La carte a été centrée sur votre position actuelle.',
          timer: 2000,
          showConfirmButton: false
        })

      } catch (error) {
        let errorMessage = 'Impossible d\'obtenir votre position.'
        let errorTitle = 'Erreur de géolocalisation'

        // Handle different error types
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorTitle = 'Autorisation refusée'
            errorMessage = 'Vous devez autoriser l\'accès à votre position pour utiliser cette fonctionnalité.'
            break
          case error.POSITION_UNAVAILABLE:
            errorTitle = 'Position indisponible'
            errorMessage = 'Votre position n\'est pas disponible. Vérifiez que le GPS est activé.'
            break
          case error.TIMEOUT:
            errorTitle = 'Délai dépassé'
            errorMessage = 'La recherche de votre position a pris trop de temps. Réessayez.'
            break
          default:
            errorMessage = 'Une erreur inconnue s\'est produite lors de la géolocalisation.'
        }

        this.$swal({
          icon: 'error',
          title: errorTitle,
          text: errorMessage,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2A6336'
        })
      } finally {
        // Small delay to ensure UI updates properly
        setTimeout(() => {
          this.gettingLocation = false
        }, 100)
      }
    },

    async saveAddress() {
      try {
        // Check if creating new address and map hasn't been interacted with
        if (!this.editingAddress && !this.mapInteracted) {
          this.$swal({
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#2A6336',
            title: "Localisation requise",
            html: `
              <div style="text-align: left; font-family: 'Montserrat', sans-serif;">
                • Veuillez placer le marqueur à l'endroit exact de livraison<br><br>
                • Vous pouvez aussi chercher votre adresse dans la barre de recherche.<br><br>
                • Ou cliquer sur « Ma position actuelle » pour qu'on repère votre adresse automatiquement
              </div>
            `,
            showCancelButton: false,
            showConfirmButton: true
          })
          return
        }

        // Validate that the coordinates are within a delivery zone
        const location = {
          lat: this.addressForm.lat,
          lng: this.addressForm.lng
        }

        const locationZone = zoneHelper.findLocationZone(location, this.zones, this.map)

        if (!locationZone) {
          this.$swal({
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#2A6336',
            title: "Hors Zone de Livraison",
            text: "L'adresse que vous avez sélectionnée se trouve en dehors de nos zones de livraison. Veuillez choisir une adresse dans une zone où nous livrons.",
            showCancelButton: false,
            showConfirmButton: true
          })
          return
        }

        // Add zone_id to the address form
        this.addressForm.zone_id = locationZone.id

        const isUpdate = !!this.editingAddress
        if (isUpdate) {
          await service.updateUserAddress(this.userId, this.editingAddress.id, this.addressForm)
        } else {
          await service.createUserAddress(this.userId, this.addressForm)
        }

        this.errors = []
        await this.loadAddresses()
        this.cancelForm()

        // Show success message
        this.$swal({
          icon: 'success',
          title: isUpdate ? 'Adresse mise à jour avec succès' : 'Adresse ajoutée avec succès',
          showConfirmButton: false,
          timer: 2000,
          toast: true,
          position: 'top-end'
        })
      } catch (err) {
        if (err.response && err.response.status === 422) {
          this.errors = err.response.data
        } else {
          console.error('Error saving address:', err)
        }
      }
    },

    async editAddress(address) {
      this.editingAddress = address
      this.showAddForm = false
      this.mapInteracted = true  // When editing, skip map interaction validation

      await this.initializeGoogleMaps()

      // Set coordinates and map center after map is fully initialized
      this.addressForm = {
        description: address.description,
        lat: parseFloat(address.lat),
        lng: parseFloat(address.lng),
        is_default: address.is_default
      }

      if (this.map && this.addressForm.lat && this.addressForm.lng) {
        this.setMapCenter(this.addressForm.lat, this.addressForm.lng)
      }
    },

    async setAsDefault(address) {
      try {
        // Immediately update UI - uncheck all others and check this one
        this.addresses.forEach(addr => {
          addr.is_default = (addr.id === address.id)
        })

        await service.setDefaultAddress(this.userId, address.id)
        await this.loadAddresses() // Refresh to ensure backend state is reflected

        // Show success popup
        this.$swal({
          icon: 'success',
          title: 'Adresse par défaut mise à jour avec succès',
          showConfirmButton: false,
          timer: 2000,
          toast: true,
          position: 'top-end'
        })
      } catch (err) {
        console.error('Error setting default address:', err)
        // Reload on error to restore correct state
        await this.loadAddresses()

        this.$swal({
          icon: 'error',
          title: 'Erreur',
          text: 'Une erreur est survenue lors de la modification de l\'adresse par défaut.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2A6336'
        })
      }
    },

    async deleteAddress(address) {
      const result = await this.$swal({
        icon: 'warning',
        title: 'Confirmer la suppression',
        text: 'Êtes-vous sûr de vouloir supprimer cette adresse ?',
        showCancelButton: true,
        confirmButtonText: 'Oui, supprimer',
        cancelButtonText: 'Annuler',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#2A6336'
      })

      if (result.isConfirmed) {
        try {
          await service.deleteUserAddress(this.userId, address.id)
          await this.loadAddresses()

          // Show success popup
          this.$swal({
            icon: 'success',
            title: 'Adresse supprimée avec succès',
            showConfirmButton: false,
            timer: 2000,
            toast: true,
            position: 'top-end'
          })
        } catch (err) {
          console.error('Error deleting address:', err)
          this.$swal({
            icon: 'error',
            title: 'Erreur',
            text: 'Une erreur est survenue lors de la suppression de l\'adresse.',
            confirmButtonText: 'OK',
            confirmButtonColor: '#2A6336'
          })
        }
      }
    },

    cancelForm() {
      this.showAddForm = false
      this.editingAddress = null
      this.addressForm = {
        description: '',
        lat: null,
        lng: null,
        is_default: false
      }
      this.errors = []
      this.mapInteracted = false  // Reset interaction flag
      this.userDragged = false  // Reset drag flag
    },

    validation_class_for(field_name) {
      if (this.errors && this.errors[field_name]) {
        return 'is-invalid'
      }
      return ''
    },

    first_error_on_attribute(field_name) {
      if (this.errors && this.errors[field_name]) {
        return this.errors[field_name][0]
      }
      return null
    }
  }
}
</script>

<style scoped>
.form-group.required .control-label:after {
  content:"*";
  color:red;
  margin-left: 3px;
}

.form-group .control-label {
  font-family: 'Recoleta';
  font-weight: 600;
  font-size: 15px;
}

/* Ensure consistent button styling */
.btn-primary {
  background-color: #2A6336 !important;
  border-color: #2A6336 !important;
  color: white !important;
}

.btn-primary:hover,
.btn-primary:focus,
.btn-primary:active {
  background-color: #1e4a26 !important;
  border-color: #1e4a26 !important;
  color: white !important;
}

.map-section {
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 15px;
  background-color: #f9f9f9;
}

.map-indication {
  color: #E97834;
  font-size: 12px;
  margin-bottom: 10px;
}

.map-container {
  position: relative;
}

.google-map {
  width: 100%;
  height: 300px;
  border-radius: 5px;
}

/* Simple center pin */
.center-marker {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -100%);
  z-index: 10;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.marker-pin {
  font-size: 30px;
  color: #2A6336;
  transition: all 0.2s ease;
}

.marker-shadow {
  width: 12px;
  height: 6px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  margin-top: 2px;
  transition: all 0.2s ease;
}

/* Dragging state */
.center-marker.dragging .marker-pin {
  transform: translateY(-4px);
}

.center-marker.dragging .marker-shadow {
  width: 16px;
  opacity: 0.6;
}

.input-search {
  position: absolute;
  width: 80%;
  top: 10px;
  left: 10px;
  font-family: 'Montserrat';
  font-size: 12px;
  font-weight: 400;
  height: 35px;
  box-sizing: border-box;
  border-radius: 5px;
  z-index: 5;
}

.input-search .input-group-text {
  background-color: #fff;
  border: none;
}

.input-search input {
  border: none;
}

.input-search input:focus {
  box-shadow: none;
}

.badge {
  font-size: 0.75em;
}

.card {
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12), 0 8px 24px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.card-title {
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.address-description {
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
}

.address-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.address-actions .btn {
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
}

.address-actions .btn i {
  margin-right: 4px;
}

.address-actions .btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Vue transition styles for smooth address reordering */
.address-card-item {
  transition: all 0.5s ease;
}

.address-list-move {
  transition: transform 0.5s ease;
}

.address-list-enter-active,
.address-list-leave-active {
  transition: all 0.5s ease;
}

.address-list-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.address-list-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Success message fade animation */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Success message styling */
.alert {
  font-family: 'Montserrat', sans-serif;
  border-radius: 8px;
  margin-bottom: 20px;
}

.alert i {
  margin-right: 8px;
}
</style>
